import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Plus, X, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
import EmailActivityTimeline from './EmailActivityTimeline';
import { apiFetch } from '../api';
import { relativeTime } from '../utils/format';
import { getEmailRecipients, saveEmailRecipients } from '../utils/emailActivity';

// Whether this server can send at all, and how the last send went. This is the
// line that would have shown the Ethereal test sink for what it was.
function DeliveryStatus({ emailActivity }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await apiFetch('/email/status');
        if (!cancelled) setStatus(next);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not read the mail status');
      }
    })();
    return () => { cancelled = true; };
  }, [emailActivity.length]);

  const last = status?.last;
  const lastRecipients = Array.isArray(last?.recipients) ? last.recipients : [];
  const tone = !status
    ? 'neutral'
    : !status.configured
      ? 'warn'
      : last?.status === 'failed'
        ? 'bad'
        : last?.status === 'sent'
          ? 'good'
          : 'neutral';
  const Icon = tone === 'good' ? CheckCircle2 : tone === 'neutral' ? Clock : AlertCircle;
  const iconClass = tone === 'good' ? 'text-[#117049]' : tone === 'bad' ? 'text-[#b42318]' : tone === 'warn' ? 'text-[#b05a0a]' : 'text-[#76829a]';

  let headline = 'Checking mail…';
  let detail = '';
  if (error) {
    headline = 'Mail status unavailable';
    detail = error;
  } else if (status && !status.configured) {
    headline = 'Mail is not set up on this server';
    detail = 'Set SMTP_HOST and EMAIL_FROM in the backend environment. Until then every send is skipped.';
  } else if (status && !last) {
    headline = `Sending as ${status.sender}`;
    detail = `through ${status.host} · nothing sent yet`;
  } else if (status && last) {
    const when = relativeTime(last.created_at || last.createdAt);
    const who = lastRecipients.length === 1 ? lastRecipients[0] : `${lastRecipients.length} people`;
    headline = last.status === 'failed'
      ? `Last send failed ${when}`
      : last.status === 'skipped'
        ? `Last send was skipped ${when}`
        : `Last sent ${when} to ${who}`;
    detail = last.status === 'failed' && last.error
      ? last.error
      : `${last.subject || 'Brief'} · sending as ${status.sender} through ${status.host}`;
  }

  return (
    <div className="bg-white rounded-lg border border-[#e1e5ec] px-4 py-3.5 flex items-start gap-3">
      <Icon size={18} className={`${iconClass} mt-0.5 flex-shrink-0`} />
      <div className="min-w-0">
        <b className="block text-[14px] font-semibold text-[#0d1524]">{headline}</b>
        {detail && <span className="block text-[12.5px] text-[#45516a] mt-0.5 break-words">{detail}</span>}
      </div>
    </div>
  );
}

function RecipientManager() {
  const [recipients, setRecipients] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await getEmailRecipients();
        if (!cancelled) setRecipients(saved);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load recipients');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const persist = async (next) => {
    const saved = await saveEmailRecipients(next);
    setRecipients(saved);
    return saved;
  };

  const addRecipient = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address');
      return;
    }
    if (recipients.includes(email)) {
      setError('Email already added');
      return;
    }
    try {
      await persist([...recipients, email]);
      setEmailInput('');
      setError('');
    } catch (err) {
      setError(err.message || 'Could not save recipient');
    }
  };

  const removeRecipient = async (email) => {
    try {
      await persist(recipients.filter(item => item !== email));
      setError('');
    } catch (err) {
      setError(err.message || 'Could not remove recipient');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-[#e1e5ec] p-[18px]">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="text-[15px] font-semibold text-[#0d1524]">Recipients</h3>
          <p className="text-xs text-[#76829a] mt-0.5">Who receives alert briefs and reports</p>
        </div>
        <span className="text-xs font-medium text-[#45516a] bg-[#f6f7fa] border border-[#e1e5ec] rounded px-2 py-0.5">
          {recipients.length}
        </span>
      </div>

      <div className="flex gap-2">
        <input
          value={emailInput}
          onChange={e => {
            setEmailInput(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={e => e.key === 'Enter' && addRecipient()}
          placeholder="user@company.com"
          className="flex-1 min-w-0 text-[13px] border border-[#e1e5ec] rounded-md px-3 focus:outline-none focus:border-[#1c4ed8] h-8"
        />
        <button
          type="button"
          onClick={addRecipient}
          className="inline-flex items-center gap-1.5 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] rounded-md hover:brightness-110 transition h-8"
        >
          <Plus size={14} /> Add
        </button>
      </div>
      {error && <p className="text-xs text-[#bd2f2b] mt-2">{error}</p>}

      <div className="mt-4 space-y-2 max-h-56 overflow-y-auto pr-1">
        {recipients.map((email, index) => (
          <div key={email} className="flex items-center gap-2 rounded-md border border-[#e1e5ec] bg-[#f6f7fa] px-3 py-1.5">
            <Mail size={13} className="text-[#76829a] flex-shrink-0" />
            <span className="text-[13px] text-[#0d1524] truncate flex-1">{email}</span>
            {index === 0 && (
              <span className="text-[11px] font-medium text-[#1c4ed8] bg-[#e7edfc] rounded px-1.5 py-0.5">
                Default
              </span>
            )}
            <button
              type="button"
              onClick={() => removeRecipient(email)}
              className="p-1.5 rounded-md text-[#76829a] hover:text-[#bd2f2b] hover:bg-[#fae8e7] transition"
              title="Remove email"
            >
              <X size={13} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailCenterView({ emailActivity = [] }) {
  const stats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCount = emailActivity.filter(a => new Date(a.createdAt) >= today).length;
    const briefCount = emailActivity.filter(a => a.type === 'brief_sent').length;
    const executiveCount = emailActivity.filter(a => a.type === 'executive_brief_sent').length;
    return { total: emailActivity.length, today: todayCount, briefs: briefCount, executive: executiveCount };
  }, [emailActivity]);

  return (
    <div className="space-y-4">
      <DeliveryStatus emailActivity={emailActivity} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        {[
          ['Total sent', stats.total],
          ['Sent today', stats.today],
          ['Executive briefs', stats.executive],
          ['Alert briefs', stats.briefs],
        ].map(([label, value]) => (
          <div key={label} className="bg-white rounded-lg border border-[#e1e5ec] px-4 py-3.5">
            <small className="block text-[13px] font-medium text-[#45516a]">{label}</small>
            <b className="block text-[28px] leading-[1.15] font-semibold tracking-[-0.03em] text-[#0d1524]">{value}</b>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_minmax(0,1fr)] gap-4 items-start">
        <RecipientManager />
        <EmailActivityTimeline activity={emailActivity} />
      </div>
    </div>
  );
}

export default EmailCenterView;
